import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { ConceptSeverity, RelationType } from '@/services/api/schemas'
import { cn } from '@/utils/cn'
import { RowFindingBadge } from './RowFindingBadge'

export interface RelationListItemProps {
  relation: RelationType
  isSelected: boolean
  onSelect: (relation: RelationType) => void
  /** Worst validation-finding severity on this relation, after a Validate run. */
  severity?: ConceptSeverity
}

/** A single relation row in the relations list column. */
export function RelationListItem({
  relation,
  isSelected,
  onSelect,
  severity,
}: RelationListItemProps) {
  const { t } = useTranslation()
  return (
    <div
      onClick={() => onSelect(relation)}
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border',
        isSelected
          ? 'bg-muted border-warning-border'
          : 'bg-card/50 border-border hover:border-border'
      )}
    >
      <ArrowRight className="h-4 w-4 text-warning-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground font-mono truncate">
          {relation.label}
        </div>
        {relation.description && (
          <div className="text-xs text-muted-foreground truncate">{relation.description}</div>
        )}
      </div>
      <RowFindingBadge severity={severity} />
      {relation.properties.length > 0 && (
        <Badge variant="outline" className="text-xs bg-accent/50 border-border shrink-0">
          {t('schemaAgent.editor.propertyCount', { count: relation.properties.length })}
        </Badge>
      )}
    </div>
  )
}
